import { Icon, Tappable, Text, useTheme } from "@rally/ui-kit";
import { useEffect, useState } from "react";
import { useStack } from "../chat/panel/panels.tsx";
import type { CallOnALine } from "../chat/person/arrivals.ts";
import { formatList } from "../localization/format.ts";
import { say } from "../localization/say.ts";
import { calledBy } from "../me/called.ts";
import { read } from "../net/rally.ts";
import type { PersonInCall } from "./calling.ts";
import { callCard } from "./resource.tsx";

export function ranFor(from: string | null, to: string | null): string {
  if (!from || !to) return "";
  const seconds = Math.max(0, Math.round((new Date(to).getTime() - new Date(from).getTime()) / 1000));
  if (seconds < 60) return say("call.seconds")(seconds);
  return say("call.minutes")(Math.round(seconds / 60));
}

function useWhoWasIn(call: string, ended: boolean): PersonInCall[] {
  const [people, setPeople] = useState<PersonInCall[]>([]);
  useEffect(() => {
    if (!ended) return;
    void read<PersonInCall[]>("call.roster", { call })
      .then(setPeople)
      .catch(() => setPeople([]));
  }, [call, ended]);
  return people;
}

export function CallLine({
  call,
  conversation,
  me,
}: {
  call: CallOnALine;
  conversation: string;
  me: string;
}) {
  const theme = useTheme();
  const stack = useStack();
  const ended = Boolean(call.endedAt);
  const people = useWhoWasIn(call.call, ended);
  const names = people.map((one) => calledBy(one) ?? one.handle);

  return (
    <Tappable
      accessibilityRole="button"
      accessibilityLabel={say("call.open")}
      onPress={() => stack.open(callCard(call.call, conversation, me))}
      testID={ended ? "call-line-ended" : "call-line-started"}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[2],
        paddingVertical: theme.space[2],
        paddingHorizontal: theme.space[2],
        borderRadius: theme.radius.md,
      }}
    >
      <Icon name={ended ? "phone-off" : "phone"} size={14} color="text.tertiary" />
      <Text typeName="caption" color="text.tertiary" numberOfLines={1} style={{ flex: 1 }}>
        {ended
          ? say("call.ended")(ranFor(call.startedAt, call.endedAt), formatList(names))
          : say("call.started")}
      </Text>
    </Tappable>
  );
}
